import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Pencil, Building2, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  useTramsAccountsForUser,
  useCreateTramsAccount,
  useUpdateTramsAccount,
} from "@/hooks/useTramsAccounts";
import type { UserTramsAccount } from "@shared/schema";

interface TramsAccountManagerProps {
  userId: number;
  userDisplayName: string;
}

interface FormState {
  profileId: string;
  agentName: string;
  interfaceId: string;
  corporateId: string;
}

const EMPTY_FORM: FormState = { profileId: "", agentName: "", interfaceId: "", corporateId: "" };

/** Whole input must be a positive safe integer ("1e3", "12.5", "12abc" are rejected). */
function parsePositiveInt(value: string): number | null {
  const trimmed = value.trim();
  if (!/^[1-9]\d*$/.test(trimmed)) return null;
  const n = Number(trimmed);
  return Number.isSafeInteger(n) ? n : null;
}

export function TramsAccountManager({ userId, userDisplayName }: TramsAccountManagerProps) {
  const { toast } = useToast();
  const { data: accounts, isLoading } = useTramsAccountsForUser(userId);
  const createAccount = useCreateTramsAccount();
  const updateAccount = useUpdateTramsAccount();

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editTarget, setEditTarget] = useState<UserTramsAccount | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  const handleAdd = async () => {
    const profileId = parsePositiveInt(form.profileId);
    if (profileId == null) {
      toast({ title: "Invalid ProfileID", description: "Must be a positive integer.", variant: "destructive" });
      return;
    }
    if (!form.agentName.trim() || !form.interfaceId.trim()) {
      toast({ title: "Missing fields", description: "All fields are required.", variant: "destructive" });
      return;
    }
    const corporateId = form.corporateId.trim() ? parsePositiveInt(form.corporateId) : undefined;
    if (corporateId === null) {
      toast({ title: "Invalid Corporate ID", description: "Must be a positive integer.", variant: "destructive" });
      return;
    }

    try {
      await createAccount.mutateAsync({
        userId,
        profileId,
        agentName: form.agentName.trim(),
        interfaceId: form.interfaceId.trim(),
        corporateId,
      });
      toast({ title: "TRAMS Account Added", description: `ProfileID ${profileId} added for ${userDisplayName}.` });
      setShowAddDialog(false);
      setForm(EMPTY_FORM);
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "Failed to add TRAMS account.", variant: "destructive" });
    }
  };

  const handleUpdate = async () => {
    if (!editTarget) return;
    if (!form.agentName.trim()) {
      toast({ title: "Missing Agent Name", description: "Agent Name is required.", variant: "destructive" });
      return;
    }

    try {
      await updateAccount.mutateAsync({
        id: editTarget.id,
        data: { agentName: form.agentName.trim() },
      });
      toast({ title: "TRAMS Account Updated" });
      setEditTarget(null);
      setForm(EMPTY_FORM);
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "Failed to update TRAMS account.", variant: "destructive" });
    }
  };

  const openEdit = (account: UserTramsAccount) => {
    setEditTarget(account);
    setForm({
      profileId: String(account.profileId),
      agentName: account.agentName,
      interfaceId: account.interfaceId,
      corporateId: account.corporateId != null ? String(account.corporateId) : "",
    });
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <div className="flex items-center gap-2">
          <Building2 className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="text-base">TRAMS Accounts</CardTitle>
        </div>
        <Button size="sm" onClick={() => { setForm(EMPTY_FORM); setShowAddDialog(true); }}>
          <Plus className="h-4 w-4 mr-1" />
          Add Account
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : !accounts || accounts.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">
            No TRAMS accounts linked. Click "Add Account" to get started.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Profile ID</TableHead>
                <TableHead>Corporate ID</TableHead>
                <TableHead>Agent Name</TableHead>
                <TableHead>Interface ID</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[60px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {accounts.map((account) => (
                <TableRow key={account.id}>
                  <TableCell className="font-mono">{account.profileId}</TableCell>
                  <TableCell className="font-mono">
                    {account.corporateId ?? <span className="text-destructive text-xs">missing</span>}
                  </TableCell>
                  <TableCell>{account.agentName}</TableCell>
                  <TableCell className="font-mono text-sm">{account.interfaceId}</TableCell>
                  <TableCell>
                    {account.isDefault ? (
                      <Badge variant="default" className="text-xs">Default</Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs">Active</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="icon" aria-label="Edit TRAMS account" className="h-8 w-8" onClick={() => openEdit(account)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {accounts && accounts.length > 1 && (
          <p className="text-xs text-muted-foreground mt-3">
            Default is automatically assigned to the account with the highest Profile ID.
          </p>
        )}
      </CardContent>

      {/* Add Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add TRAMS Account</DialogTitle>
            <DialogDescription>
              Link a TRAMS profile to {userDisplayName}. The account with the highest Profile ID will be set as default.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="profileId">Profile ID</Label>
              <Input
                id="profileId"
                type="number"
                min={1}
                placeholder="e.g. 12345"
                value={form.profileId}
                onChange={(e) => setForm((f) => ({ ...f, profileId: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="agentName">Agent Name</Label>
              <Input
                id="agentName"
                placeholder="e.g. Jane Smith Travel"
                value={form.agentName}
                onChange={(e) => setForm((f) => ({ ...f, agentName: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="interfaceId">Interface ID</Label>
              <Input
                id="interfaceId"
                placeholder="e.g. TRAMS-001"
                value={form.interfaceId}
                onChange={(e) => setForm((f) => ({ ...f, interfaceId: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="corporateId">Corporate ID</Label>
              <Input
                id="corporateId"
                type="number"
                min={1}
                placeholder="e.g. 12346 — required for invoicing"
                value={form.corporateId}
                onChange={(e) => setForm((f) => ({ ...f, corporateId: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleAdd} disabled={createAccount.isPending}>
              {createAccount.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Add Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editTarget} onOpenChange={(open) => !open && setEditTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit TRAMS Account</DialogTitle>
            <DialogDescription>
              Profile ID and Interface ID are immutable TRAMS identifiers. Only Agent Name can be updated.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-muted-foreground">Profile ID</Label>
              <p className="text-sm font-mono mt-1">{editTarget?.profileId}</p>
            </div>
            <div>
              <Label className="text-muted-foreground">Interface ID</Label>
              <p className="text-sm font-mono mt-1">{editTarget?.interfaceId}</p>
            </div>
            <div>
              <Label htmlFor="editAgentName">Agent Name</Label>
              <Input
                id="editAgentName"
                value={form.agentName}
                onChange={(e) => setForm((f) => ({ ...f, agentName: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)}>Cancel</Button>
            <Button onClick={handleUpdate} disabled={updateAccount.isPending}>
              {updateAccount.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
